import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'

import { Working } from '~/components/working'
import { reached, removed, useBeat } from '~/components/use-work'
import { AmountStep } from '~/components/amount-step'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { FoodMark } from '~/components/food-mark'
import { mealDeck } from '~/data/food-emoji'
import { CountedTextarea } from '~/components/counted-textarea'
import { FoodDials } from '~/components/food-dials'
import { FoodSearch, MyFoodsList } from '~/components/food-search'
import {
  ChevronLeftIcon,
  DeleteIcon,
  NoteIcon,
  PlusIcon,
  RedoIcon,
  SaveIcon,
  SendIcon,
  ShareIcon,
  StarIcon,
  UndoIcon,
} from '~/components/icons'
import { MealSlotList, type SlotAt } from '~/components/meal-members'
import { NutrientFields } from '~/components/nutrient-fields'
import { HeaderAction, PageDialog } from '~/components/page-dialog'
import { Tab } from '~/components/recipe-form-dialog'
import { useToast } from '~/components/toast-context'
import { recordDiaryStep } from '~/lib/diary-undo'
import { WhenPill } from '~/components/when-pill'
import { useAuth } from '~/auth/auth-context'
import { useApi, useDataService } from '~/data/data-context'
import { useAskingForForm, useFavouriteMeals, useMealLogs } from '~/data/food-store'
import {
  EMPTY_MEAL_STATE,
  EMPTY_NUTRIENTS,
  dayTotal,
  entryLogged,
  macroLine,
  mealTotal,
  foodKey,
  membersOn,
  nowTime,
  nutrientsFor,
  standingOn,
  stateTrimmedTo,
  type Amount,
  type Entry,
  type Food,
  type GoalOnDay,
  type Ingredient,
  type Meal,
  type MealLog,
  type MealSlot,
  type MealState,
  type NutritionData,
} from '~/data/nutrition'
import { logEntries, reloadNutrition } from '~/data/nutrition-store'
import { todayKey } from '~/data/weighins'
import { logMomentSaid, loggedMoment } from '~/lib/meal-moment'
import { handOver, saidOf, sentFromMeal, sharedFromMeal } from '~/lib/share-food'
import { openingAmount } from '~/lib/opening-amount'
import {
  EMPTY_NUTRIENT_TEXT,
  nutrientText,
  nutrientsTyped,
  type NutrientText,
} from '~/lib/nutrient-text'

/** The one amount a quick entry counts as. */
const QUICK_AMOUNT: Amount = { value: 1, unit: 'portion', portion: 'serving' }

/** How many logs the page shows before offering the rest — the nutrition spec, Logged before. */
/** When a mark was made. Wrapped so the rule that keeps a render pure can see it is not one. */
function markedNow(): number {
  return Date.now()
}

const LOGS_SHOWN = 5

/**
 * How long a change has to stand still before the page writes it down — the same quiet a run
 * of typing needs to count as one step to undo, so one typed amount is one write and not one
 * a letter.
 */
const WRITING_SETTLES = 400

/** The whole of what the page holds at one moment: what a step back goes back over. */
interface PageStep {
  readonly name: string
  readonly slots: readonly MealSlot[]
  readonly state: MealState
}

/**
 * A meal still being asked about — the spec's Asking the AI.
 *
 * What the AI makes of a meal is a meal, so it is read on this page like any other. What is
 * different is that it is not settled: it can be asked again. Given, this is what the page
 * offers in place of everything that is about a meal that exists.
 */
export interface MealAsking {
  /** True while the AI is being asked again, which puts every way of changing it down. */
  busy: boolean
  details: string
  detailing: boolean
  onDetails: (details: string) => void
  onDetailing: (out: boolean) => void
  /** Asking again, with the answer as the page stands — every slot that is on, by its slot. */
  onAskAgain: (standing: readonly { slotId: string; member: Ingredient }[]) => void
  /** The details field, so whoever owns the asking can hand it the cursor as it comes out. */
  detailsRef?: RefObject<HTMLTextAreaElement | null>
}

/**
 * The meal's page, per the spec's Meals: where a meal is read, changed and logged, there being
 * no form apart from it. Its slots listed as the alternative each stands on, headed by what
 * they come to together; food added from the page; saving, which is the only thing that keeps
 * what has been done here; the times it was logged under its slots; and the key that logs
 * every slot that is on, each as its own entry.
 *
 * It is where an AI answer is read as well — see [MealAsking] — and where a shared meal opens,
 * and where something logged without being kept is opened again: a meal that has not been
 * kept, and drawing it anywhere else was two pages saying the same thing in two hands.
 */
export function MealPage({
  meal,
  exists,
  day,
  entries = [],
  goal = null,
  data = null,
  time,
  sharedBy,
  unkeptLogId,
  onClose,
  onLogged,
  onKept,
  onDeleted,
  asking,
  sending = false,
  riders,
  logLabel,
  sentToYou = false,
  plainSlots = false,
  inPanel = false,
  onAtStake,
}: {
  /** The meal as it opens, or null for one being made. */
  meal: Meal | null
  /** Whether the meal is the account's own; false for one not yet saved as one. */
  exists: boolean
  day: string
  /**
   * The day's entries, for what the day would have left; none where the way in has no day, and
   * null where this device has not read the day yet — the rings then draw nothing rather than
   * drawing a day nobody has read as an empty one.
   */
  entries?: readonly Entry[] | null
  goal?: GoalOnDay | null
  /** The diary, for the calendar the day what is logged lands on is picked from. */
  data?: NutritionData | null
  /** What is logged lands at this time; now where the way in named none. */
  time?: string
  /** Who shared it, where the meal came from a link. */
  sharedBy?: string | undefined
  /** The log the meal was opened from among the foods logged before, so logging again moves it. */
  unkeptLogId?: string | undefined
  /** Putting the page away with nothing logged. */
  onClose: () => void
  /**
   * The meal logged: logging is over, and whatever is behind this page goes with it.
   *
   * Handed the day it landed on and the page as it stood when the key was pressed: whoever
   * opened a link acts on both afterwards — leaving for the diary on that day, keeping the
   * foods it carries, making a meal of it — and the page can be sent to another day before the
   * key is pressed, so neither can be read off what arrived.
   */
  onLogged: (landed: string, standing: Meal) => void
  /** Something not yet kept was kept, and the page is now that meal's. */
  onKept?: ((meal: Meal) => void) | undefined
  /** The meal was deleted; the page has left. */
  onDeleted?: ((id: string) => void) | undefined
  /** Given, the meal is an answer still being asked about — see [MealAsking]. */
  asking?: MealAsking
  /**
   * The page as the way to put a plate together for somebody else — the nutrition spec,
   * Sending food. The same page throughout: what changes is that its key sends, that it asks
   * for no name, and that leaving it after it has been sent asks nothing.
   */
  sending?: boolean
  /**
   * Controls standing over the key, where the page is something somebody handed over — keeping
   * the foods it carries, making a meal of it. They belong to whoever opened the link rather
   * than to this page, which only finds them room: what they do happens in `onLogged`.
   */
  riders?: ReactNode
  /** What the key says, where the way in wants it said differently. */
  logLabel?: string
  /**
   * Whether what the page is about was *sent* rather than shared — the nutrition spec, Sending
   * and sharing. Entries somebody handed over to log, so the page never offers keeping them as
   * a meal: what they are is a moment in a day, and the library gains only what the riders ask
   * for.
   */
  sentToYou?: boolean
  /**
   * Whether the slots are a plain list: what is on them, in amounts, with no alternatives to
   * turn to and nothing to turn off. True of a plate and of entries somebody sent — both are
   * occasions rather than meals kept and logged again.
   */
  plainSlots?: boolean
  /**
   * Drawn in the logging panel rather than as a page over the diary — how a way of logging
   * stands on web, see the nutrition spec, Logging an entry. The panel is the page's chrome:
   * the lit glyph is its title, and the panel is put away from outside it, so the page has no
   * way out of its own and says instead, through [onAtStake], what going would lose.
   */
  inPanel?: boolean
  /** Told whether leaving the page as it stands would lose something, as that changes. */
  onAtStake?: ((atStake: boolean) => void) | undefined
}) {
  const api = useApi()
  const service = useDataService()
  const toast = useToast()
  const { session } = useAuth()
  const creating = meal === null
  const [mealId] = useState(() => (meal === null || !exists ? crypto.randomUUID() : meal.id))
  const [name, setName] = useState(meal?.name ?? '')
  const [slots, setSlots] = useState<readonly MealSlot[]>(meal?.slots ?? [])
  const [state, setState] = useState<MealState>(() =>
    stateTrimmedTo(meal?.state ?? EMPTY_MEAL_STATE, meal?.slots ?? []),
  )
  const [logs, setLogs] = useState<readonly MealLog[]>(meal?.logs ?? [])
  /** Every log of it the device holds, which is where more of them are read from. */
  const heldLogs = useMealLogs(mealId)
  const [logCount] = useState(meal?.logCount ?? 0)
  const [logsShown, setLogsShown] = useState(LOGS_SHOWN)
  /** What a step back goes back to, oldest first, and what a step on goes on to, nearest first. */
  const [past, setPast] = useState<readonly PageStep[]>([])
  const [future, setFuture] = useState<readonly PageStep[]>([])
  const [adjusting, setAdjusting] = useState<SlotAt | null>(null)
  /** The slots as they stood when an amount began to be changed: the whole run is one step. */
  const [adjustFrom, setAdjustFrom] = useState<readonly MealSlot[] | null>(null)
  const [quick, setQuick] = useState<{
    name: string
    text: NutrientText
    at: SlotAt | null
  } | null>(null)
  const [adding, setAdding] = useState<'search' | 'mine' | null>(null)
  /** Which slot what is being added goes into — null for a slot of its own. */
  const [addTo, setAddTo] = useState<string | null>(null)
  const [measuring, setMeasuring] = useState<{ food: Food; amount: Amount } | null>(null)
  /** Logged: the page is on its way out, so a second press does not log it twice. */
  const [logging, setLogging] = useState(false)
  const [keeping, setKeeping] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [marking, setMarking] = useState(false)
  /** Which of the two hand-overs is under way — each works on its own control. */
  const [sharing, setSharing] = useState<'send' | 'share' | null>(null)
  /**
   * The page as it stood when it was last handed to somebody, or null where it never was.
   * Leaving on exactly that is not an abandonment — sending was the job — so nothing is asked
   * about it; change anything afterwards and it is worth asking again.
   */
  const [handedOver, setHandedOver] = useState<string | null>(null)
  /** The id logging this page writes its log under, where the meal is not yet one. */
  const [unkeptId] = useState(() => unkeptLogId ?? crypto.randomUUID())
  const [loggedUnkept, setLoggedUnkept] = useState(unkeptLogId !== undefined)
  /** What was handed in, so an answer asked again is told from the one standing. */
  const givenRef = useRef(meal)
  /** The name as a run of typing began, so the run is one step back rather than one a letter. */
  const nameBefore = useRef<string | null>(null)
  /** When what is logged lands: the day and time the page was opened for, changed on its foot. */
  const [when, setWhen] = useState(() => ({ day, time: time ?? nowTime() }))

  const step = (): PageStep => ({ name, slots, state })

  /** A change that is a step of its own: the page as it stood is kept to go back to. */
  const record = (change: Partial<PageStep>) => {
    setPast((held) => [...held, step()])
    setFuture([])
    if (change.name !== undefined) setName(change.name)
    if (change.slots !== undefined) setSlots(change.slots)
    if (change.state !== undefined) setState(change.state)
  }

  const standOn = (at: PageStep) => {
    setName(at.name)
    setSlots(at.slots)
    setState(stateTrimmedTo(at.state, at.slots))
  }

  const undo = () => {
    const back = past[past.length - 1]
    if (back === undefined) return
    setPast(past.slice(0, -1))
    setFuture([step(), ...future])
    standOn(back)
  }

  const redo = () => {
    const on = future[0]
    if (on === undefined) return
    setFuture(future.slice(1))
    setPast([...past, step()])
    standOn(on)
  }

  /*
   * An answer asked again arrives as a new meal handed in, and the page is put to it as a
   * step of its own — the nutrition spec, Asking the AI.
   */
  useEffect(() => {
    if (meal === null || meal === givenRef.current || asking === undefined) {
      return
    }
    givenRef.current = meal
    setPast((held) => [...held, { name, slots, state }])
    setFuture([])
    setName(meal.name)
    setSlots(meal.slots)
    setState(stateTrimmedTo(meal.state, meal.slots))
    // The page follows the meal handed in; what it stood at is on the stack to go back to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meal])

  /*
   * The meal as it was last written. On a meal the account holds the page writes itself as it
   * is changed, so this is what the writing is measured against and never lags behind for
   * long; on one it does not hold yet, nothing is written anywhere, so this is what leaving
   * would lose.
   */
  const [lastSaved, setLastSaved] = useState<string>(() => JSON.stringify(step()))
  /** Whether the page has made a meal of what was not one: it is the account's own from then. */
  const [savedHere, setSavedHere] = useState(false)
  /** Whether the account holds this meal, however the page came to be about it. */
  const mine = exists || savedHere

  /*
   * Whether it is one of the marked ones — null until this device has read the account, and
   * on a page that is not about a meal at all, there being nothing to mark.
   */
  const heldMealFavourites = useFavouriteMeals()
  const marked =
    !mine || heldMealFavourites === null
      ? null
      : heldMealFavourites.some((each) => each.id === mealId)

  const on = membersOn(slots, state)
  /*
   * The picker the meal opens for itself asks as a form — `/specs/features/food-order.spec.md`:
   * its plate is what the meal already holds, the alternatives its slots stand on, so what goes
   * with them stands first and the time of day says nothing about it.
   */
  const pickerAsking = useAskingForForm(
    on.flatMap((member) => (member.food === null ? [] : [foodKey(member.food)])),
  )
  const total = mealTotal(on)
  const unsaid = on.find((member) => member.food !== null && member.amount.value <= 0)
  const whyNot =
    name.trim() === '' && !sending
      ? 'Give the meal a name'
      : slots.length === 0
        ? 'Add something to it'
        : on.length === 0
          ? 'Everything is left out'
          : unsaid !== undefined
            ? `${unsaid.name} has no amount yet`
            : ''
  const loggable = whyNot === '' && !logging && !keeping && asking?.busy !== true
  const askingShown = useBeat(asking?.busy === true)
  const sendingShown = useBeat(sharing === 'send')
  const dirtyAt = (at: PageStep): boolean => JSON.stringify(at) !== lastSaved
  const dirty = dirtyAt(step())
  /*
   * Whether saving would do anything, and could. Saving is about a meal the account does not
   * hold yet: the press makes it. It is the logging bar and nothing else.
   */
  const saveable = loggable && !mine
  /** Whether logging would make the meal as well, which the key says out loud. */
  const alsoSaves = !mine && creating

  /** Whether leaving the page as it stands would lose something — see [askToClose]. */
  const losesOnLeaving = (): boolean => {
    // An amount half changed is not kept by leaving: it goes back to what it was, and the
    // write on the way out is of that.
    const standing = adjustFrom === null ? step() : { ...step(), slots: adjustFrom }
    /*
     * A meal the account holds loses nothing by being left — it is written as it is changed,
     * and a change still inside its quiet goes out as the page does. The nutrition spec, What
     * a meal remembers. One that does not exist yet has everything on it at stake, and so does
     * one holding something it could not write — see [heldBack].
     *
     * A plate already sent is nothing at stake either: it went where it was going, and what is
     * still on the page is what went — the nutrition spec, Sending food.
     */
    return (
      (mine ? heldBack(standing) : dirtyAt(standing)) && JSON.stringify(standing) !== handedOver
    )
  }

  const askToClose = () => {
    if (losesOnLeaving()) {
      // Asked first, with the change still on the page for whoever answers "stay".
      setLeaving(true)
    } else {
      putBackAndLeave()
    }
  }

  /** An amount half changed put back, so nothing half typed stands on the page. */
  const putTheAmountBack = () => {
    if (adjustFrom === null) {
      return
    }
    /*
     * The page may be going in the same breath, so what is left waiting to be written is said
     * here rather than left to an effect that will not run again: the page as it will stand
     * once the amount is back, which is not the page that was showing the half-typed one.
     */
    const standing = { ...step(), slots: adjustFrom }
    unwrittenYet.current = mine && dirtyAt(standing) ? standing : null
    setSlots(adjustFrom)
    setAdjustFrom(null)
    setAdjusting(null)
  }

  /** Leaving with that put back, which is the ordinary way out. */
  const putBackAndLeave = () => {
    putTheAmountBack()
    onClose()
  }

  /**
   * Leaving by answering "discard", which is the one way out that throws something away. What
   * the page was holding because it could not write it goes with the page — otherwise the
   * flush on the way out would send the very change the press was made to be rid of, and the
   * dialog would read as having been ignored.
   *
   * After the put-back, which sets the ref itself.
   */
  const discardAndLeave = () => {
    putTheAmountBack()
    unwrittenYet.current = null
    writeFailed.current = false
    onClose()
  }

  /** A slot turned to an alternative, or off — where it stands, and not a step. */
  const turn = (slotId: string, altId: string | null) => {
    setState((held) =>
      altId === null
        ? { chosen: held.chosen, off: [...new Set([...held.off, slotId])] }
        : {
            chosen: { ...held.chosen, [slotId]: altId },
            off: held.off.filter((id) => id !== slotId),
          },
    )
  }

  /** One alternative put right — its amount, or a quick entry's figures. */
  const replace = (at: SlotAt, member: Ingredient) => {
    record({
      slots: slots.map((slot) =>
        slot.id === at.slotId
          ? {
              ...slot,
              alternatives: slot.alternatives.map((alt) =>
                alt.id === at.altId ? { ...alt, member } : alt,
              ),
            }
          : slot,
      ),
    })
  }

  /** What was picked, landing as a slot of its own, or as an alternative of the slot named. */
  const add = (member: Ingredient) => {
    if (addTo === null) {
      record({
        slots: [
          ...slots,
          {
            id: crypto.randomUUID(),
            alternatives: [{ id: crypto.randomUUID(), member }],
          },
        ],
      })
      return
    }
    const alt = { id: crypto.randomUUID(), member }
    record({
      slots: slots.map((slot) =>
        slot.id === addTo ? { ...slot, alternatives: [...slot.alternatives, alt] } : slot,
      ),
      // The slot turns to what was just added, and stands on: adding to a slot is choosing it.
      state: {
        chosen: { ...state.chosen, [addTo]: alt.id },
        off: state.off.filter((id) => id !== addTo),
      },
    })
  }

  /**
   * An alternative taken off for good; a slot whose last alternative goes goes with it. A slot
   * standing on the one that goes stands on its neighbour after — the one after it, or the
   * one before it where it was the last — rather than on nothing.
   */
  const remove = (at: SlotAt) => {
    const chosen = { ...state.chosen }
    const kept = slots.flatMap((slot) => {
      if (slot.id !== at.slotId) return [slot]
      const stood = standingOn(slot, state)
      const alternatives = slot.alternatives.filter((alt) => alt.id !== at.altId)
      if (alternatives.length === 0) return []
      if (stood !== null && stood.id === at.altId) {
        const index = Math.min(slot.alternatives.indexOf(stood), alternatives.length - 1)
        const next = alternatives[index]
        if (next !== undefined) chosen[slot.id] = next.id
      }
      return [{ ...slot, alternatives }]
    })
    record({ slots: kept, state: stateTrimmedTo({ chosen, off: state.off }, kept) })
  }

  /**
   * The page set to how the meal stood at a log — the nutrition spec, Logged before. A slot
   * or an alternative still in the meal takes what the log says of it, one removed since is
   * brought back as it was, and one added since stays and is turned off.
   */
  const apply = (log: MealLog) => {
    const now = new Map(slots.map((slot) => [slot.id, slot]))
    const restored = log.slots.map((then) => {
      const current = now.get(then.id)
      if (current === undefined) return then
      const alternatives = [
        ...current.alternatives.map(
          (alt) => then.alternatives.find((each) => each.id === alt.id) ?? alt,
        ),
        ...then.alternatives.filter(
          (alt) => !current.alternatives.some((each) => each.id === alt.id),
        ),
      ]
      return { ...current, alternatives }
    })
    const thenIds = new Set(log.slots.map((slot) => slot.id))
    const added = slots.filter((slot) => !thenIds.has(slot.id))
    const next = [...restored, ...added]
    record({
      slots: next,
      state: stateTrimmedTo(
        { chosen: log.state.chosen, off: [...log.state.off, ...added.map((slot) => slot.id)] },
        next,
      ),
    })
  }

  /*
   * Every log of the meal is held on the device, so reading more of them is showing more of
   * what is already here rather than fetching a page. A meal that arrived carrying only its
   * last few keeps those until the mirror has the rest.
   */
  const moreLogs = () => {
    setLogsShown((held) => held + LOGS_SHOWN)
    if (heldLogs !== null && heldLogs.length > logs.length) {
      setLogs(heldLogs)
    }
  }

  const asMeal = (): Meal => ({
    id: mealId,
    name: name.trim(),
    emoji: null,
    slots,
    state,
    members: on,
    logs,
    logCount,
    lastLoggedAt: meal?.lastLoggedAt ?? null,
  })

  /*
   * Logging the meal never waits on the server — the nutrition spec, Logging an entry. Its
   * entries stand on the day and are queued in the same press, the page goes, and what the
   * meal itself remembers of it is sent behind the page rather than holding the key down.
   */
  const logMeal = () => {
    if (!loggable) return
    setLogging(true)
    const at = when.time
    const onDay = when.day
    /*
     * Which meal the entries name as the one they came from — the nutrition spec, Logging. A
     * meal the account holds, or one being made, which this same press writes: either way the
     * page's id is a meal there will be. What was logged without being kept names none; there
     * is no meal to open, and an id pointing at nothing is worse than nothing.
     */
    const loggedFrom = mine || creating ? mealId : null
    // Counted up through the slots, so the entries stand on the day in the meal's own order.
    const written = Date.now()
    const made = on.map((each, index) =>
      entryLogged(
        each.food === null
          ? {
              day: onDay,
              time: at,
              name: each.name,
              food: null,
              amount: each.amount,
              nutrients: each.nutrients,
              fromMeal: true,
              mealId: loggedFrom,
            }
          : {
              day: onDay,
              time: at,
              food: each.food,
              amount: each.amount,
              fromMeal: true,
              mealId: loggedFrom,
            },
        written + index,
      ),
    )
    logEntries(made)
    /*
     * A meal is one press however many slots it stands on, so one undo takes the whole of it
     * off the day again — the nutrition spec, Undoing. The meal itself, and the log saying it
     * was had, are not undone: what was eaten is the diary's, and the meal is the library's.
     */
    recordDiaryStep({
      said: `${name.trim()} logged`,
      changes: made.map((entry) => ({ id: entry.id, before: null, after: entry })),
    })
    const log = {
      name: name.trim(),
      emoji: null,
      slots,
      state,
      loggedAt: loggedMoment(onDay, at),
    }
    const didNotLand = () => {
      toast('That did not reach us. Try again in a moment.')
    }
    if (mine || creating) {
      /*
       * Logging a meal the account holds writes it as well, so what was logged is what the
       * meal opens on next time — the nutrition spec, What a meal remembers. A meal being
       * made exists once it is logged, which is the same write.
       *
       * This is the page's latest word, so a debounced write still in the air is overtaken
       * by it — see [write] — and the page counts as written from here: it is going, and a
       * write left waiting for its going would send the same meal a second time.
       */
      writing.current += 1
      unwrittenYet.current = null
      setLastSaved(JSON.stringify(step()))
      setSavedHere(true)
      api
        .putMeal(mealId, { name: log.name, emoji: null, slots, state })
        .then(() => api.logMeal(mealId, { id: crypto.randomUUID(), ...log }))
        .then(reloadNutrition, didNotLand)
    } else if (!sentToYou) {
      /*
       * A meal logged without being kept still stands among the foods logged before, to be
       * logged again or kept — the nutrition spec, Meals wherever they stand.
       *
       * Entries somebody sent are not that. They were never a meal, and an entry share is
       * never kept as itself — the nutrition spec, Opening an entry share — so a log of one
       * would put a thing in the library nobody asked for, under a name they did not choose,
       * offering to be logged again as a meal that never existed.
       */
      setLoggedUnkept(true)
      api.putUnkeptLog(unkeptId, log).then(reloadNutrition, didNotLand)
    }
    toast(
      mine || creating
        ? `${name.trim()} logged.`
        : on.length === 1
          ? `${on[0]?.name ?? 'Food'} logged.`
          : `${String(on.length)} entries logged.`,
    )
    onLogged(when.day, asMeal())
  }

  /*
   * What the page has still to write, held where the page going away can still reach it — the
   * project's rule on a write outliving the screen that asked for it. The press that changes
   * something is very often the press that leaves: a menu item dismisses its menu, a
   * confirmation closes its dialog, and a write waiting out its quiet would go with them.
   */
  const unwrittenYet = useRef<PageStep | null>(null)

  /** Whether the meal has been deleted, after which nothing more is written about it. */
  const deleted = useRef(false)

  /**
   * Whether the last write was refused. There is no queue behind these on the web the way
   * there is on Android, so a page whose write did not land is holding the only copy of it —
   * which makes leaving it data loss, and something to ask about.
   */
  const writeFailed = useRef(false)

  /**
   * Which write is the page's latest. `lastSaved` only moves once the server has answered, so
   * the page stays dirty for the whole flight and a second change can put a second write in
   * the air beside the first. Only the latest one's answer is listened to: an older write
   * failing after a newer one succeeded would otherwise leave the older page waiting to be
   * flushed, and the way out would send it over the top of what the server had just confirmed.
   */
  const writing = useRef(0)

  /**
   * Sending one, at whatever moment it is reached. Nothing is said when it lands, a write
   * nobody asked for having no news in it.
   *
   * A write that failed is put back rather than forgotten: `lastSaved` moves only once the
   * server has it, so the page goes on counting as unwritten, the next change sends the whole
   * of it again, and leaving sends it once more on the way out. There is no queue behind this
   * on the web the way there is on Android, so the page itself is the only thing holding what
   * has not landed — and dropping it here would lose exactly the amount this whole change set
   * out to remember.
   */
  const write = (at: PageStep) => {
    if (deleted.current) {
      /*
       * Deleted, and the page is going. A write let through here would arrive after the delete
       * and put the meal back — the same press deletes and leaves, so a change still inside
       * its quiet is exactly what would do it.
       */
      unwrittenYet.current = null
      return
    }
    unwrittenYet.current = null
    const said = (writing.current += 1)
    api
      .putMeal(mealId, {
        name: at.name.trim(),
        emoji: null,
        slots: at.slots,
        state: at.state,
      })
      .then(() => {
        // Overtaken: a later write is the page's word now, whatever this one has to say.
        if (said !== writing.current) {
          return
        }
        writeFailed.current = false
        setLastSaved(JSON.stringify(at))
      })
      .catch(() => {
        if (said !== writing.current) {
          return
        }
        writeFailed.current = true
        unwrittenYet.current = at
        toast('That did not reach us. Try again in a moment.')
      })
  }

  /*
   * The page writes itself: what it shows goes to the account once a change has stood still —
   * the nutrition spec, What a meal remembers. Nobody presses this, and nothing is said when
   * it lands, a write nobody asked for having no news in it; a write that failed is still
   * worth saying, the page having shown the change already.
   *
   * A blank name is the one thing held back: the meal keeps the name it had until it has one
   * again. Everything else the page can stand at is written as it stands, a meal with nothing
   * left in it included — what a meal remembers is what its page shows.
   */
  useEffect(() => {
    if (!mine || !dirty) {
      /*
       * Nothing to write. An amount half changed and then put back lands here, and what was
       * waiting is let go rather than carried to the page's going: the page stands where the
       * account already does — which is also the answer to whether the last write failed, so
       * the two are let go together. Left standing, the mark would have the next change ask on
       * the way out about something that was going to be written perfectly well.
       */
      unwrittenYet.current = null
      writeFailed.current = false
      return
    }
    if (name.trim() === '') {
      /*
       * Held back rather than let go: the meal keeps the name it had, and so does everything
       * else done meanwhile, until there is a name to write it under. What is waiting stays
       * waiting, so leaving asks about it — see [askToClose].
       */
      return
    }
    const at = step()
    unwrittenYet.current = at
    const timer = window.setTimeout(() => {
      write(at)
    }, WRITING_SETTLES)
    return () => {
      window.clearTimeout(timer)
    }
    // The page as it stands is the whole of what this watches; `step` reads all of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, dirty, name, slots, state])

  /**
   * Whether the page is holding something it *cannot* write, rather than something it merely
   * has not written yet. A change still inside its quiet is safe — the page sends it on the
   * way out — but a blank name holds the whole page back, and a write the server refused has
   * nowhere else to live. Either way, leaving loses what is on screen.
   */
  const heldBack = (at: PageStep): boolean =>
    mine && dirtyAt(at) && (at.name.trim() === '' || writeFailed.current)

  /*
   * Said upward where the page cannot ask for itself: in the panel, a press outside is one the
   * page never sees, so whatever puts the panel away is the one that has to ask.
   */
  useEffect(() => {
    // After every render rather than on a list of what it reads: the answer rests on a ref
    // as well — a write that was refused — and a ref moving says nothing to a dependency list.
    onAtStake?.(losesOnLeaving())
  })

  /*
   * The page going, with a change still waiting out its quiet: it is sent now rather than
   * dropped. Without this, changing something and leaving inside the quiet left a device
   * showing the change and a server that never heard about it, and the next refresh quietly
   * put it back.
   */
  useEffect(
    () => () => {
      const waiting = unwrittenYet.current
      if (waiting !== null) {
        write(waiting)
      }
    },
    // Only the page's going, and `write` reads what it needs off the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  /**
   * Saving what is not the account's own yet as one of its meals, as the page stands — the
   * nutrition spec. A meal being made becomes that meal where it stands and the page goes on
   * being about it; one handed in has a meal made of it, and whatever opened the page turns
   * to that meal.
   */
  const saveToLibrary = async () => {
    if (mine || !saveable) return
    setKeeping(true)
    const at = step()
    /*
     * Nothing to overtake here, unlike the logging path: this runs only on a meal the account
     * does not hold yet, and the page writes itself for no such meal — so there is never a
     * write of its own in the air. See [write].
     */
    try {
      const saved = await api.putMeal(mealId, {
        name: at.name.trim(),
        emoji: null,
        slots: at.slots,
        state: at.state,
        adopt: loggedUnkept ? [unkeptId] : [],
      })
      setLastSaved(JSON.stringify(at))
      setSavedHere(true)
      toast(`${saved.name} saved to your meals.`)
      onKept?.(saved)
    } catch {
      toast('That did not reach us. Try again in a moment.')
    } finally {
      setKeeping(false)
    }
  }

  /*
   * The mark lands in the mirror first and is sent after. A send that fails is put back here
   * rather than left to the next pull: the row never moved on the server, so the feed carries
   * nothing about it and a pull would leave the wrong mark standing for good.
   */
  const mark = async () => {
    if (marked === null) return
    setMarking(true)
    const held = asMeal()
    /*
     * The row as it stands, so undoing a failed unmarking puts back what was marked rather
     * than the page as it happens to look now — a favourite is the copy taken when it was
     * made, and writing this page's unsaved edits into it would invent a copy the server has
     * never seen and nothing would ever correct.
     */
    const standing = service.getState().mealFavourites?.find((each) => each.mealId === mealId)
    try {
      if (marked) {
        await service.drop('mealFavourite', mealId)
        await api.removeMealFavourite(mealId)
      } else {
        await service.keep('mealFavourite', mealId, {
          mealId,
          meal: { ...held, logs: [], logCount: 0, lastLoggedAt: null },
          createdAt: markedNow(),
        })
        await api.addMealFavourite(mealId, held)
      }
    } catch {
      if (marked && standing !== undefined) {
        await service.keep('mealFavourite', mealId, standing)
      } else if (!marked) {
        await service.drop('mealFavourite', mealId)
      }
      toast('Favourites need a connection.')
    } finally {
      setMarking(false)
    }
  }

  /**
   * The meal handed to somebody to keep, as its page stands — the nutrition spec, Sharing from
   * the app. A blueprint: what arrives is the meal, and the key on the other side keeps it.
   */
  const share = async () => {
    if (sharing !== null) return
    setSharing('share')
    try {
      const held = asMeal()
      const shared = sharedFromMeal(held, session?.user.name ?? null)
      const outcome = await handOver(shared, saidOf(shared, total, session?.user.name ?? null))
      if (outcome === 'copied') {
        toast('Link copied')
      } else if (outcome === 'too-large') {
        toast('There is too much here to share as a link.')
      } else if (outcome === 'failed') {
        toast('That could not be shared.')
      }
    } finally {
      setSharing(null)
    }
  }

  /**
   * What is on the page handed to somebody to log — the nutrition spec, Sending from the app.
   * Logging done for somebody else, so it carries the moment the page stands on and the key on
   * the other side logs rather than keeps.
   *
   * This is also the whole of what the page that exists only to make a plate for somebody does,
   * which is why it asks for no name: an entry share is named after what it holds.
   */
  const send = async () => {
    if (sharing !== null) return
    setSharing('send')
    try {
      /*
        A plate lands on the moment it was sent — the nutrition spec, Sending food — since it
        says no day or time of its own to land on instead. Sending from a meal that exists
        carries the moment that page is logging onto, which is a real answer worth keeping.
      */
      const at = sending ? { day: todayKey(), time: nowTime() } : { day, time: time ?? nowTime() }
      const shared = sentFromMeal(asMeal(), session?.user.name ?? null, at)
      const outcome = await handOver(shared, saidOf(shared, total, session?.user.name ?? null))
      if (outcome === 'copied') {
        toast('Link copied')
      } else if (outcome === 'too-large') {
        toast('There is too much here to send as a link.')
      } else if (outcome === 'failed') {
        toast('That could not be sent.')
      } else {
        /*
         * Sending was what the page was for, so leaving it afterwards is not an abandonment
         * and is not asked about — the nutrition spec, Sending food. Without this, composing a
         * plate for somebody ends with the app asking whether to discard the thing you just
         * successfully handed over.
         */
        setHandedOver(JSON.stringify(step()))
      }
    } finally {
      setSharing(null)
    }
  }

  const removeMeal = async () => {
    /*
     * Said before the delete goes, not after: the same press leaves the page, so a change
     * still inside its quiet must not be sent after the delete and put the meal back. Saying
     * it here is enough — [write] is the one way anything goes, and it stops on this.
     */
    deleted.current = true
    try {
      await reached(() => removed(() => api.deleteMeal(mealId)))
    } catch (error) {
      /*
       * Nothing navigated away, so the page is still standing on a meal that still exists.
       * Taken back, or it would go on refusing to write anything for the rest of its life —
       * and said in the confirmation it was asked from, which stays to be tried again.
       */
      deleted.current = false
      throw error
    }
    toast(`${name.trim()} deleted.`)
    onDeleted?.(mealId)
  }

  const opened =
    adjusting === null
      ? null
      : (slots
          .find((slot) => slot.id === adjusting.slotId)
          ?.alternatives.find((alt) => alt.id === adjusting.altId) ?? null)
  /*
   * What the rest of the day comes to, or null while this device is still reading the day —
   * the dials then draw the amount alone rather than drawing it against a day nobody has read,
   * which would say nothing had been logged on it.
   */
  const rest = entries === null ? null : dayTotal(entries)
  const quickTyped = quick === null ? null : nutrientsTyped(quick.text)
  const keepQuick = () => {
    if (quick === null || quickTyped === null || quick.name.trim() === '') return
    const member: Ingredient = {
      name: quick.name.trim(),
      food: null,
      amount: QUICK_AMOUNT,
      nutrients: { ...EMPTY_NUTRIENTS, ...quickTyped },
    }
    if (quick.at === null) {
      add(member)
    } else {
      const held = slots
        .find((slot) => slot.id === quick.at?.slotId)
        ?.alternatives.find((alt) => alt.id === quick.at?.altId)
      replace(quick.at, { ...member, amount: held?.member.amount ?? QUICK_AMOUNT })
    }
    setQuick(null)
    setAdding(null)
    setAddTo(null)
  }

  const box =
    'w-full rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent'
  /*
   * What the page is called. A plate is not a meal and is never named as one: it says what it
   * is for, since it carries no name of its own to say instead — the nutrition spec, Sending
   * food.
   */
  const title = sending
    ? 'Send food'
    : name.trim() === ''
      ? creating
        ? 'New meal'
        : 'Meal'
      : name.trim()
  const inside = measuring !== null || quick !== null || adding !== null || opened !== null
  const backOut = () => {
    if (measuring !== null) {
      setMeasuring(null)
    } else if (quick !== null) {
      setQuick(null)
    } else if (adding !== null) {
      setAdding(null)
      setAddTo(null)
    } else {
      // Backed out of rather than kept: the amount goes back to what it was.
      if (adjustFrom !== null) setSlots(adjustFrom)
      setAdjustFrom(null)
      setAdjusting(null)
    }
  }

  /*
   * The page is headed by the meal itself: its deck, its name edited where it stands, and at
   * the far end what can be done to it — the nutrition spec, Meals. A page inside it — an
   * alternative being measured, food being added — is headed by that instead.
   */
  const heading = inside
    ? { title }
    : {
        title,
        titleBelow: true,
        actions: (
          <>
            <HeaderAction
              label="Undo"
              Icon={UndoIcon}
              disabled={asking?.busy === true || past.length === 0}
              onPress={undo}
            />
            <HeaderAction
              label="Redo"
              Icon={RedoIcon}
              disabled={asking?.busy === true || future.length === 0}
              onPress={redo}
            />
            {/*
             * Saving is about a meal the account does not hold yet: the press makes it. A meal
             * it does hold carries none, there being nothing a press could write that the page
             * has not written already — the nutrition spec, What a meal remembers.
             *
             * A plate being put together for somebody else has none of this either: it is never
             * kept, never favourited and never deleted, and its one key sends. What is left in
             * the heading is undoing and redoing, which belong to the putting-together itself.
             */}
            {!mine && !sending && !sentToYou && (
              <HeaderAction
                label={sharedBy === undefined ? 'Save as a meal' : 'Keep in my library'}
                Icon={SaveIcon}
                accent
                disabled={!saveable && !keeping}
                working={keeping}
                onPress={() => {
                  void saveToLibrary()
                }}
              />
            )}
            {/*
             * Sending: what is on the page handed to somebody to log — the nutrition spec,
             * Sending from the app. Offered on every meal page of this account's own making,
             * and not on one opened from a link, where the page is already about somebody
             * else's handing-over.
             */}
            {!sending && !sentToYou && sharedBy === undefined && (
              <HeaderAction
                label="Send"
                Icon={SendIcon}
                disabled={sharing === 'share' || on.length === 0}
                working={sharing === 'send'}
                onPress={() => {
                  void send()
                }}
              />
            )}
            {/*
              Sharing: the meal handed to somebody to keep, whether it has been saved or not,
              since a link carries what is on the page — the nutrition spec, Sharing from the
              app. Not on a plate, which is never kept; not on the AI's answer, which the spec
              gives nothing to share; not on a page opened from a link, which is already about
              somebody else's handing-over; and not on one with nothing on it, a link carrying
              no members being one neither reader will open.
            */}
            {!sending &&
              !sentToYou &&
              asking === undefined &&
              sharedBy === undefined &&
              on.length > 0 && (
                <HeaderAction
                  label="Share"
                  Icon={ShareIcon}
                  disabled={sharing === 'send'}
                  working={sharing === 'share'}
                  onPress={() => {
                    void share()
                  }}
                />
              )}
            {mine && !sending && (
              <>
                <HeaderAction
                  label={marked === true ? 'Remove from favourites' : 'Add to favourites'}
                  Icon={(props) => <StarIcon {...props} filled={marked === true} />}
                  pressed={marked === true}
                  accent={marked === true}
                  disabled={marked === null || marking}
                  onPress={() => {
                    void mark()
                  }}
                />
                <HeaderAction
                  label="Delete"
                  Icon={DeleteIcon}
                  danger
                  onPress={() => {
                    setDeleting(true)
                  }}
                />
              </>
            )}
          </>
        ),
      }

  /*
   * The panel along the page's foot — the nutrition spec, Turning a slot on web: when what is
   * logged will land, changed there, and the key that logs. Part of the page's chrome rather
   * than of what scrolls, so it stands at the foot whatever is scrolled under it.
   */
  const foot = inside ? undefined : (
    <div className="space-y-3">
      {/*
        What the link offers besides logging, where the page is something somebody sent: a
        rider is a choice about the press, not a rival to it — the nutrition spec, Opening an
        entry share. So it stands over the key rather than beside it, and the key still does
        everything in one.
      */}
      {riders}
      <div className="flex items-center gap-3">
        {/*
          When what is logged will land. A plate says nothing of the kind: it is not being
          logged here at all, and what is sent lands on the moment it was sent — the nutrition
          spec, Sending food.
        */}
        {!sending && (
          <WhenPill
            day={when.day}
            time={when.time}
            data={data}
            onDay={(next) => {
              setWhen((held) => ({ ...held, day: next }))
            }}
            onTime={(next) => {
              setWhen((held) => ({ ...held, time: next }))
            }}
          />
        )}
        <p
          className="min-w-0 flex-1 truncate text-xs text-ink-muted"
          aria-label="Why the meal cannot be logged"
        >
          {whyNot}
        </p>
        {asking?.detailing === true ? (
          <button
            type="button"
            aria-busy={asking.busy || undefined}
            disabled={!asking.busy && asking.details.trim() === ''}
            className="relative shrink-0 rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-ink shadow-2xl shadow-black/40 disabled:opacity-50"
            onClick={() => {
              if (asking.busy) return
              asking.onAskAgain(
                slots
                  .filter((slot) => !state.off.includes(slot.id))
                  .flatMap((slot) => {
                    const alt = standingOn(slot, state)
                    return alt === null ? [] : [{ slotId: slot.id, member: alt.member }]
                  }),
              )
            }}
          >
            <Working shown={askingShown}>Ask again</Working>
          </button>
        ) : (
          <button
            type="button"
            aria-busy={(sending && sharing === 'send') || undefined}
            disabled={sending ? sharing === null && on.length === 0 : !loggable}
            className="relative shrink-0 rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-ink shadow-2xl shadow-black/40 disabled:opacity-50"
            onClick={() => {
              if (sending) {
                void send()
              } else {
                logMeal()
              }
            }}
          >
            {/*
            A plate for somebody else has one act and the key says it. Everywhere else the key
            logs, and says whether the press also saves — the nutrition spec, Logging.
          */}
            <Working shown={sending && sendingShown}>
              {sending
                ? 'Send'
                : (logLabel ??
                  (alsoSaves ? 'Log & save' : asking === undefined ? 'Log meal' : 'Log all'))}
            </Working>
          </button>
        )}
      </div>
    </div>
  )

  const page = (
    <>
      {measuring !== null ? (
        <div className="space-y-4">
          <h2 className="text-lg font-medium">{measuring.food.name}</h2>
          <AmountStep
            food={measuring.food}
            amount={measuring.amount}
            onAmount={(next) => {
              setMeasuring({ ...measuring, amount: next })
            }}
          />
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm"
              onClick={() => {
                setMeasuring(null)
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={measuring.amount.value <= 0}
              className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
              onClick={() => {
                const nutrients = nutrientsFor(measuring.food, measuring.amount)
                if (nutrients === null) return
                add({
                  name: measuring.food.name,
                  food: measuring.food,
                  amount: measuring.amount,
                  nutrients,
                })
                setMeasuring(null)
                setAdding(null)
                setAddTo(null)
              }}
            >
              Add to the meal
            </button>
          </div>
        </div>
      ) : quick !== null ? (
        <div className="space-y-4">
          <h2 className="text-lg font-medium">{quick.at === null ? 'Quick entry' : quick.name}</h2>
          <label className="block">
            <span className="mb-1 block text-sm text-ink-muted">Name</span>
            <input
              aria-label="Name"
              value={quick.name}
              onChange={(event) => {
                setQuick({ ...quick, name: event.target.value })
              }}
              className={box}
            />
          </label>
          <NutrientFields
            text={quick.text}
            onText={(next) => {
              setQuick({ ...quick, text: next })
            }}
          />
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm"
              onClick={() => {
                setQuick(null)
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={quick.name.trim() === '' || quickTyped === null}
              className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
              onClick={keepQuick}
            >
              {quick.at === null ? 'Add to the meal' : 'Save'}
            </button>
          </div>
        </div>
      ) : adding !== null ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Tab
              label="Search"
              on={adding === 'search'}
              onPick={() => {
                setAdding('search')
              }}
            />
            <Tab
              label="Mine"
              on={adding === 'mine'}
              onPick={() => {
                setAdding('mine')
              }}
            />
            <Tab
              label="Quick entry"
              on={false}
              onPick={() => {
                setQuick({ name: '', text: EMPTY_NUTRIENT_TEXT, at: null })
              }}
            />
            <span className="flex-1" />
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm"
              onClick={() => {
                setAdding(null)
                setAddTo(null)
              }}
            >
              Cancel
            </button>
          </div>
          {adding === 'search' ? (
            <FoodSearch
              picked={new Set()}
              asking={pickerAsking}
              onPick={(food) => {
                setMeasuring({ food, amount: openingAmount(food, null) })
              }}
            />
          ) : (
            <MyFoodsList
              picked={new Set()}
              asking={pickerAsking}
              onPick={(food) => {
                setMeasuring({ food, amount: openingAmount(food, null) })
              }}
            />
          )}
        </div>
      ) : opened !== null && adjusting !== null && opened.member.food !== null ? (
        <div className="space-y-4">
          <h2 className="text-lg font-medium">{opened.member.name}</h2>
          <AmountStep
            food={opened.member.food}
            amount={opened.member.amount}
            onAmount={(next) => {
              const food = opened.member.food
              if (food === null) return
              setSlots((held) =>
                held.map((slot) =>
                  slot.id === adjusting.slotId
                    ? {
                        ...slot,
                        alternatives: slot.alternatives.map((alt) =>
                          alt.id === adjusting.altId
                            ? {
                                ...alt,
                                member: {
                                  ...alt.member,
                                  amount: next,
                                  nutrients: nutrientsFor(food, next) ?? alt.member.nutrients,
                                },
                              }
                            : alt,
                        ),
                      }
                    : slot,
                ),
              )
            }}
            goal={goal}
            dayTotal={rest}
          />
          <div className="flex justify-end">
            <button
              type="button"
              disabled={opened.member.amount.value <= 0}
              className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
              onClick={() => {
                // The whole run of changing an amount is one step back.
                const before = adjustFrom ?? slots
                if (JSON.stringify(before) !== JSON.stringify(slots)) {
                  setPast((held) => [...held, { name, slots: before, state }])
                  setFuture([])
                }
                setAdjustFrom(null)
                setAdjusting(null)
              }}
            >
              Keep
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/*
            The meal names its own page, under the heading: its deck, read off its name and the
            slots that are on and following both, and its name edited where it stands — a field
            wearing the name's own type until it is pressed.

            A plate has neither. It is what it holds and nothing else — the nutrition spec,
            Sending food — and what is sent is named after that, so a name to type would be a
            thing to answer about something nobody is going to keep.
          */}
          {!sending && (
            <div className="flex items-center gap-3">
              <FoodMark deck={mealDeck({ name, slots, state })} size="size-12 text-2xl" />
              {/* A field rather than a line, so a long name wraps instead of running off the
                end of the page — the name is never cut to fit the room it was given. */}
              <textarea
                aria-label="Name"
                placeholder="Name the meal"
                rows={1}
                value={name}
                disabled={asking?.busy === true}
                onChange={(event) => {
                  setName(event.target.value)
                }}
                onFocus={(event) => {
                  nameBefore.current = name
                  event.target.select()
                }}
                onBlur={() => {
                  // A run of typing is one step back, written down as the run ends.
                  const before = nameBefore.current
                  nameBefore.current = null
                  if (before !== null && before !== name) {
                    setPast((held) => [...held, { ...step(), name: before }])
                    setFuture([])
                  }
                }}
                onKeyDown={(event) => {
                  // A name is one line of writing however many lines it takes to draw.
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    event.currentTarget.blur()
                  }
                }}
                className="min-w-0 flex-1 resize-none rounded-lg bg-transparent px-2 py-1 text-2xl font-medium field-sizing-content outline-none transition-colors hover:bg-hover focus:bg-surface-raised focus:ring-2 focus:ring-accent"
              />
            </div>
          )}
          {/* What they all come to together, following the slots as they are changed — drawn
              the way a food's page draws what an amount comes to: the same dials, against the
              same day, over the slots they are the answer to. */}
          <FoodDials
            comesTo={on.length === 0 ? null : total}
            dayTotalWithout={rest}
            goal={goal}
            label="Together"
          />
          {sharedBy !== undefined && (
            <p className="text-sm text-ink-muted">
              {sentToYou ? 'Sent' : 'Shared'} by {sharedBy}
            </p>
          )}
          {slots.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-ink-muted">Nothing in it yet.</p>
          ) : (
            <MealSlotList
              slots={slots}
              state={state}
              plain={sending || plainSlots}
              disabled={asking?.busy === true}
              estimated={asking !== undefined}
              onTurn={turn}
              onAdjust={(at) => {
                const alt = slots
                  .find((slot) => slot.id === at.slotId)
                  ?.alternatives.find((each) => each.id === at.altId)
                if (alt === undefined) return
                if (alt.member.food === null) {
                  setQuick({ name: alt.member.name, text: nutrientText(alt.member.nutrients), at })
                } else {
                  setAdjustFrom(slots)
                  setAdjusting(at)
                }
              }}
              onAmount={(at, next) => {
                setSlots((held) =>
                  held.map((slot) =>
                    slot.id === at.slotId
                      ? {
                          ...slot,
                          alternatives: slot.alternatives.map((alt) =>
                            alt.id === at.altId
                              ? {
                                  ...alt,
                                  member: {
                                    ...alt.member,
                                    amount: next,
                                    nutrients:
                                      alt.member.food === null
                                        ? alt.member.nutrients
                                        : (nutrientsFor(alt.member.food, next) ??
                                          alt.member.nutrients),
                                  },
                                }
                              : alt,
                          ),
                        }
                      : slot,
                  ),
                )
              }}
              // The whole run of typing an amount is one step back, written down as it ends.
              onAmountStart={() => {
                setAdjustFrom(slots)
              }}
              onAmountDone={() => {
                const before = adjustFrom ?? slots
                if (JSON.stringify(before) !== JSON.stringify(slots)) {
                  setPast((held) => [...held, { name, slots: before, state }])
                  setFuture([])
                }
                setAdjustFrom(null)
              }}
              onAddTo={(slotId) => {
                setAddTo(slotId)
                setAdding('search')
              }}
              onRemove={remove}
            />
          )}
          {/*
           * Adding to the meal, as a card under what it holds — the phone's own way in: the
           * mark over the words, both in the middle, and the card lit in the accent under the
           * pointer. It stands close under the slots, being about them.
           */}
          <button
            type="button"
            aria-label="Add food"
            disabled={asking?.busy === true}
            className="-mt-4 flex w-full flex-col items-center gap-1.5 rounded-2xl border border-dashed border-white/20 px-4 py-4 text-sm text-ink-muted transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent disabled:opacity-40"
            onClick={() => {
              setAddTo(null)
              setAdding('search')
            }}
          >
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-surface-raised ring-1 ring-white/10">
              <PlusIcon aria-hidden className="size-5" />
            </span>
            Add food
          </button>
          {asking !== undefined && <AskingAgain asking={asking} />}
          {mine && logs.length > 0 && (
            <section className="space-y-1" aria-label="Logged before">
              <h3 className="px-3 text-xs font-medium tracking-wider text-ink-muted uppercase">
                Logged before
              </h3>
              <ul className="space-y-0.5">
                {logs.slice(0, logsShown).map((log) => {
                  const onThen = membersOn(log.slots, log.state)
                  const when = logMomentSaid(log.loggedAt)
                  return (
                    <li key={log.id}>
                      <button
                        type="button"
                        aria-label={`Set to how it was logged, ${when}, ${macroLine(mealTotal(onThen))}, ${onThen.map((each) => each.name).join(', ')}`}
                        className="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition-colors hover:bg-hover"
                        onClick={() => {
                          apply(log)
                        }}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm">{when}</span>
                          <span className="block truncate text-xs text-ink-muted">
                            {onThen.length === 0
                              ? 'Nothing was on'
                              : onThen.map((each) => each.name).join(', ')}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs text-ink-muted">
                          {macroLine(mealTotal(onThen)).split(' ·')[0]}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
              {(logCount > logsShown || logs.length > logsShown) && (
                <button
                  type="button"
                  className="rounded-full px-3 py-1 text-xs text-accent transition-colors hover:bg-hover"
                  onClick={moreLogs}
                >
                  Show more
                </button>
              )}
            </section>
          )}
        </div>
      )}
      {leaving && (
        <ConfirmDialog
          title={
            sending
              ? 'Discard this plate?'
              : creating
                ? 'Discard this meal?'
                : 'Discard your changes?'
          }
          body={
            sending
              ? 'It has not been sent to anybody.'
              : creating && !mine
                ? 'Nothing has been logged, and the meal is not saved.'
                : 'What you changed here is not saved.'
          }
          confirm="Discard"
          onConfirm={discardAndLeave}
          onDismiss={() => {
            setLeaving(false)
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={`Delete ${name.trim()}?`}
          body="Entries already logged from it keep what they say. The meal itself goes."
          confirm="Delete"
          onConfirm={removeMeal}
          onDismiss={() => {
            setDeleting(false)
          }}
        />
      )}
    </>
  )

  /*
   * In the panel, the panel is the frame: the way back and what can be done to the page along
   * its top, what the page holds scrolling under that, and the key pinned to its foot the way
   * the quick entry's is. What the page is called is the lit glyph over it, so it says it only
   * for ears — and on a page inside it, where the way back needs something to lead back from.
   */
  if (inPanel) {
    return (
      <section aria-label={title} className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center gap-1 px-2 pt-2">
          {inside ? (
            <>
              <button type="button" aria-label="Back" className="icon-btn" onClick={backOut}>
                <ChevronLeftIcon aria-hidden className="size-7" />
              </button>
              <h2 className="min-w-0 flex-1 truncate text-lg font-medium">{title}</h2>
            </>
          ) : (
            <span className="flex-1" />
          )}
          {'actions' in heading && (
            <div className="flex shrink-0 items-center gap-0.5">{heading.actions}</div>
          )}
        </div>
        <div className={`min-h-0 flex-1 overflow-y-auto p-3 ${foot === undefined ? '' : 'pb-44'}`}>
          {page}
        </div>
        {foot !== undefined && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-surface-overlay via-surface-overlay/95 to-transparent px-3 pt-10 pb-3 [&>*]:pointer-events-auto">
            {foot}
          </div>
        )}
      </section>
    )
  }

  return (
    <PageDialog
      {...heading}
      onClose={askToClose}
      className="max-w-xl"
      fixedHeight
      {...(foot === undefined ? {} : { foot })}
      {...(inside ? { onBack: backOut } : {})}
    >
      {page}
    </PageDialog>
  )
}

/**
 * Asking the AI again: the further details, folded away behind the control that offers them,
 * and asked for only once that is pressed — the nutrition spec, Asking the AI.
 */
function AskingAgain({ asking }: { asking: MealAsking }) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-label={asking.detailing ? 'Hide details' : 'Add details'}
        aria-pressed={asking.detailing}
        aria-expanded={asking.detailing}
        disabled={asking.busy}
        className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-accent transition-colors hover:bg-hover disabled:opacity-50"
        onClick={() => {
          asking.onDetailing(!asking.detailing)
        }}
      >
        <NoteIcon aria-hidden className="size-4" />
        {asking.detailing ? 'Hide details' : 'Add details'}
      </button>
      {asking.detailing && (
        <CountedTextarea
          label="Details"
          placeholder="The rice was brown, and there were two of them"
          rows={2}
          value={asking.details}
          onValue={asking.onDetails}
          disabled={asking.busy}
          {...(asking.detailsRef === undefined ? {} : { fieldRef: asking.detailsRef })}
        />
      )}
    </div>
  )
}
